import { graphql } from 'react-apollo';

import { useQuery } from '@apollo/client';
import { CareerAcademy_Variant as CareerAcademyVariant } from '__generated__/graphql-types';
import gql from 'graphql-tag';

import waitForGraphQL from 'js/lib/waitForGraphQL';

import type { ProgramCreationPageQuery_EnterpriseProgramsV1Resource_slug_elements as EnterpriseProgram } from 'bundles/enterprise-admin-program-creation/components/__generated__/ProgramCreationPageQuery';
import type { Program } from 'bundles/enterprise-admin-program-home/types';
import type { EnterpriseProgramFromSlugFragment } from 'bundles/enterprise-legacy-learner-home/queries/__generated__/EnterpriseProgramBySlugQuery';
import type TEnterpriseProgramsV1 from 'bundles/naptimejs/resources/enterprisePrograms.v1';
import GetCareerAcademyVariantByOrgSlug from 'bundles/unified-career-academy/queries/GetCareerAcademyVariantByOrgSlug.graphql';
import GetCareerAcademyVariantByProgram from 'bundles/unified-career-academy/queries/GetCareerAcademyVariantByProgram.graphql';
import GetProgramCareerAcademyEligibility from 'bundles/unified-career-academy/queries/GetProgramCareerAcademyEligibilityQuery.graphql';
import type {
  GetCareerAcademyVariantByOrgSlugQuery,
  GetCareerAcademyVariantByOrgSlugQueryVariables,
} from 'bundles/unified-career-academy/queries/__generated__/GetCareerAcademyVariantByOrgSlug';
import type {
  GetCareerAcademyVariantByProgramQuery,
  GetCareerAcademyVariantByProgramQueryVariables,
} from 'bundles/unified-career-academy/queries/__generated__/GetCareerAcademyVariantByProgram';
import type {
  GetProgramCareerAcademyEligibilityQuery,
  GetProgramCareerAcademyEligibilityQueryVariables,
} from 'bundles/unified-career-academy/queries/__generated__/GetProgramCareerAcademyEligibilityQuery';
import type { PropCareerAcademyVariant } from 'bundles/unified-career-academy/utils/helpers';

export const getProgramCareerAcademyVariantQueryGraphQl = <
  TInputProps extends {
    program?: Program | EnterpriseProgramFromSlugFragment | EnterpriseProgram | TEnterpriseProgramsV1;
    programId?: string;
  },
>(
  blockingCall?: boolean
) => {
  const graphqlToUse = blockingCall ? waitForGraphQL : graphql;

  return graphqlToUse<
    TInputProps,
    GetCareerAcademyVariantByProgramQuery,
    GetCareerAcademyVariantByProgramQueryVariables,
    PropCareerAcademyVariant
  >(GetCareerAcademyVariantByProgram, {
    skip: ({ program, programId }) => !program && !programId,
    options: ({ program, programId }) => ({
      variables: {
        programId: program ? (program.id as string) : (programId as string),
      },
      context: {
        clientName: 'gatewayGql',
      },
    }),
    props: ({ data }) => {
      const variant = data?.CareerAcademy?.queryVariantByProgramId;

      return {
        careerAcademyVariant: variant,
        refetchCareerAcademyVariant: data?.refetch,
        isCareerAcademyVariantLoading: data?.loading,
      };
    },
  });
};

export const useProgramCareerAcademyVariantQuery = (programId?: string) => {
  const { data, refetch, loading } = useQuery<
    GetCareerAcademyVariantByProgramQuery,
    GetCareerAcademyVariantByProgramQueryVariables
  >(GetCareerAcademyVariantByProgram, {
    skip: !programId,
    variables: {
      // we can assert non-null because of the skip clause
      programId: programId!,
    },
    context: { clientName: 'gatewayGql' },
  });

  const variant = data?.CareerAcademy?.queryVariantByProgramId;

  return {
    careerAcademyVariant: variant,
    refetchCareerAcademyVariant: refetch,
    isCareerAcademyVariantLoading: loading,
  };
};

export const useProgramCareerAcademyEligibilityQuery = ({
  programId,
  variant,
}: {
  programId?: string;
  variant?: CareerAcademyVariant;
}) => {
  const { data } = useQuery<GetProgramCareerAcademyEligibilityQuery, GetProgramCareerAcademyEligibilityQueryVariables>(
    GetProgramCareerAcademyEligibility,
    {
      skip: !programId,
      variables: {
        // we can assert non-null because of the skip clause
        programId: programId!,
        variant: variant ?? CareerAcademyVariant.Default,
      },
      context: { clientName: 'gatewayGql' },
    }
  );
  const isCareerAcademyEligible = data?.CareerAcademy?.queryProgramEligibilityByIdAndVariant?.eligible;

  return isCareerAcademyEligible;
};

export const getOrgCareerAcademyVariantQueryGraphQl = <TInputProps extends { orgSlug?: string }>() => {
  return graphql<
    TInputProps,
    GetCareerAcademyVariantByOrgSlugQuery,
    GetCareerAcademyVariantByOrgSlugQueryVariables,
    PropCareerAcademyVariant
  >(GetCareerAcademyVariantByOrgSlug, {
    skip: ({ orgSlug }) => !orgSlug,
    options: ({ orgSlug }) => ({
      variables: {
        // we can assert non-null because of the skip clause
        orgSlug: orgSlug!,
      },
      context: {
        clientName: 'gatewayGql',
      },
    }),
    props: ({ data }) => {
      const variant = data?.CareerAcademy?.queryVariantByOrganizationSlug;

      return {
        careerAcademyVariant: variant,
        refetchCareerAcademyVariant: data?.refetch,
        isCareerAcademyVariantLoading: data?.loading,
      };
    },
  });
};

export const useOrgCareerAcademyVariantQuery = (orgSlug?: string) => {
  const { data, refetch, loading } = useQuery<
    GetCareerAcademyVariantByOrgSlugQuery,
    GetCareerAcademyVariantByOrgSlugQueryVariables
  >(GetCareerAcademyVariantByOrgSlug, {
    skip: !orgSlug,
    variables: {
      // we can assert non-null because of the skip clause
      orgSlug: orgSlug!,
    },
    context: { clientName: 'gatewayGql' },
  });

  const variant = data?.CareerAcademy?.queryVariantByOrganizationSlug;

  return {
    careerAcademyVariant: variant,
    refetchCareerAcademyVariant: refetch,
    isCareerAcademyVariantLoading: loading,
  };
};

export const ProgramBySlugQuery = gql`
  query ProgramBySlugQuery($slug: String!) {
    ProgramsV1Resource {
      slug(slug: $slug) {
        elements {
          id
          program {
            ... on ProgramsV1_enterpriseProgramMember {
              enterpriseProgram {
                metadata {
                  slug
                  name
                }
              }
            }
          }
          curriculum {
            ... on ProgramsV1_tracksCurriculumMember {
              tracksCurriculum {
                coreTracks {
                  id
                  title
                  isLocked
                  subjectId
                  roleId
                  curriculumItems {
                    ... on ProgramsV1_domainIdMember {
                      domainId {
                        id
                      }
                    }
                  }
                }
                electiveTracks {
                  id
                  title
                  isLocked
                  subjectId
                  roleId
                  curriculumItems {
                    ... on ProgramsV1_domainIdMember {
                      domainId {
                        id
                      }
                    }
                  }
                }
              }
            }
            ... on ProgramsV1_wildcardCurriculumMember {
              wildcardCurriculum {
                electiveTracks {
                  id
                  title
                  isLocked
                  subjectId
                  roleId
                  curriculumItems {
                    ... on ProgramsV1_courseIdMember {
                      courseId {
                        id
                      }
                    }
                  }
                }
              }
            }
          }
        }
      }
    }
  }
`;
